import { TranslationKeys } from 'Constants/TranslationKeys'
import { renderTranslation } from 'Helpers/GenericHelpers'
import { handleUpdateSettings, showError } from 'Helpers/SettingsHelper'
import { getTokenFromSessionStorage } from 'Helpers/TokenHelper'
import {
  RespondingEntity,
  setAnswers,
  setRespondingEntity,
  setSettings,
  setSettingsNames,
  setSpecificSetting,
} from 'Slices/Settings/SettingsSlice'

const { createRequest } = require('../../Helpers/GenericHelpers')

const getSettings = () => async (dispatch, getState) => {
  const state = getState()
  const token = getTokenFromSessionStorage()

  if (!state.present.article.serverConfig || !token) {
    return new Promise(resolve => {
      resolve([])
    })
  }

  return await createRequest(
    `/api/Settings/questions?config=${state.present.article.serverConfig}`,
    'GET',
    null,
    token,
    true,
    true,
  )
    .then(response => response.json())
    .then(data => {
      if (data) {
        dispatch(setSettings(data))
      }
    })
}

const getSpecificSetting = settingsName => (dispatch, getState) => {
  const state = getState()
  const token = getTokenFromSessionStorage()

  if (!state.present.article.serverConfig || !token) {
    return new Promise(resolve => {
      resolve([])
    })
  }

  return createRequest(
    `/api/Settings/questions/${settingsName}?config=${state.present.article.serverConfig}`,
    'GET',
    null,
    token,
    true,
    true,
  )
    .then(response => response.json())
    .then(data => {
      if (data) {
        dispatch(setSpecificSetting({ settingsName: settingsName, data: data }))
      }
    })
}

const getSettingsNames = () => (dispatch, getState) => {
  const state = getState()
  const token = getTokenFromSessionStorage()

  if (!state.present.article.serverConfig || !token) {
    return new Promise(resolve => {
      resolve([])
    })
  }

  createRequest(
    `/api/Settings/questions/getSettingsNames?config=${state.present.article.serverConfig}`,
    'GET',
    null,
    token,
    true,
    true,
  )
    .then(response => response.json())
    .then(data => {
      if (data) {
        dispatch(setSettingsNames(data))
      }
    })
}

const getAnswers = (settingsName: string) => (dispatch, getState) => {
  const state = getState()
  const token = getTokenFromSessionStorage()

  if (!state.present.article.serverConfig || !token) {
    return Promise.resolve([])
  }

  return new Promise(resolve => {
    createRequest(
      `/api/Settings/answers/${settingsName}?config=${state.present.article.serverConfig}`,
      'GET',
      null,
      token,
      true,
      true,
    )
      .then(response => response.json())
      .then(data => {
        if (data) {
          // Dispatch both actions and return a promise that resolves when they are done
          Promise.all([
            dispatch(getSpecificSetting(settingsName)),
            dispatch(setAnswers({ settingsName: settingsName, data: data })),
          ]).then(() => {
            resolve([])
          })
        }
      })
  })
}

const deleteAnswers = () => async (dispatch, getState) => {
  const state = getState()
  const token = getTokenFromSessionStorage()
  const deletedAnswers = state.present.settings.deletedAnswers
  if (!state.present.article.serverConfig || !token) {
    return Promise.resolve([])
  }

  const deletePromises = Object.entries(deletedAnswers).map(async ([key, value]) => {
    const settingsName = key
    const body = value

    try {
      const response = await createRequest(
        `/api/Settings/deleteAnswers/${settingsName}?config=${state.present.article.serverConfig}`,
        'POST',
        body,
        token,
        true,
        true,
      )
      const data = await response.json()

      if (data?.success) {
        return settingsName
      }
    } catch (error) {
      console.error('Error:', error)
    }
  })

  return Promise.all(deletePromises)
}

const sendAnswers = () => async (dispatch, getState) => {
  const state = getState()
  const token = getTokenFromSessionStorage()
  const editedAnswers = state.present.settings.editedAnswers
  if (!state.present.article.serverConfig || !token) {
    return Promise.resolve([])
  }

  const sendPromises = Object.entries(editedAnswers).map(async ([key, value]) => {
    const settingsName = key
    const body = value

    try {
      const response = await createRequest(
        `/api/Settings/setAnswers/${settingsName}?config=${state.present.article.serverConfig}`,
        'POST',
        body,
        token,
        true,
        true,
      )
      const data = await response.json()
      if (data?.success) {
        return settingsName
      }
    } catch (error) {
      console.error('Error:', error)
    }
  })

  return Promise.all(sendPromises)
}

const addRespondingEntity = (settingsName: string, newName: string) => (dispatch, getState) => {
  const state = getState()
  const token = getTokenFromSessionStorage()
  const language = state.present.language.value

  if (!state.present.article.serverConfig || !token || !settingsName) {
    return new Promise(resolve => {
      resolve([])
    })
  }

  createRequest(
    `/api/Settings/${settingsName}/addRespondingEntity/${newName}?config=${state.present.article.serverConfig}`,
    'POST',
    null,
    token,
    true,
    true,
  )
    .then(response => response.json())
    .then(data => {
      if (data) {
        dispatch(setRespondingEntity({ settingsName: settingsName, data: data }))
        handleUpdateSettings(dispatch, [settingsName])
      }
    })
    .catch(err => showError(err, renderTranslation(language, TranslationKeys.warningRespondingEntityNameUsed)))
}

const editRespondingEntity = (settingsName: string, respondingEntity: RespondingEntity) => (dispatch, getState) => {
  const state = getState()
  const token = getTokenFromSessionStorage()
  const language = state.present.language.value

  if (!state.present.article.serverConfig || !token || !settingsName) {
    return new Promise(resolve => {
      resolve([])
    })
  }

  const body = respondingEntity

  createRequest(
    `/api/Settings/${settingsName}/EditRespondingEntity?config=${state.present.article.serverConfig}`,
    'POST',
    body,
    token,
    true,
    true,
  )
    .then(response => response.json())
    .then(data => {
      if (data?.success) {
        handleUpdateSettings(dispatch, [settingsName])
      }
    })
    .catch(err => showError(err, renderTranslation(language, TranslationKeys.warningRespondingEntityNameUsed)))
}

const deleteRespondingEntity = (settingsName: string, id: string) => (dispatch, getState) => {
  const state = getState()
  const token = getTokenFromSessionStorage()

  if (!state.present.article.serverConfig || !token || (!settingsName && !id)) {
    return new Promise(resolve => {
      resolve([])
    })
  }

  createRequest(
    `/api/Settings/${settingsName}/deleteRespondingEntity/${id}?config=${state.present.article.serverConfig}`,
    'POST',
    null,
    token,
    true,
    true,
  )
    .then(response => response.json())
    .then(data => {
      if (data?.success) {
        handleUpdateSettings(dispatch, [settingsName])
      }
    })
}

export {
  getSettings,
  getAnswers,
  sendAnswers,
  addRespondingEntity,
  editRespondingEntity,
  deleteRespondingEntity,
  deleteAnswers,
  getSpecificSetting,
  getSettingsNames,
}
